import {generateRandomString} from "../../helpers/stringHelpers";
import {cloneDeep, isEmpty, debounce} from "lodash";
import AddressAppResource from "@milestoneslabs/milestones-core-js/src/AppResources/AddressAppResource";
import AppResourceCollection from '@milestoneslabs/milestones-js-engine/src/Data/AppResourceCollection';

import CityAndStateMixin from "../../mixins/CityAndStateMixin";
import ShowNotificationMixin from "../../mixins/ShowNotificationMixin";
import {standardizeAddress} from '../../helpers/coreLogic';

const ZIP_CODE_LENGTH = 5;
const IS_ZIP_CODE_VALID_EVENT = 'is-zip-code-valid';
const DELIVERY_LINE = 'deliveryLine';
const CITY = 'city';
const STATE = 'state';
const ADDRESS_SEARCH_PARTS = [DELIVERY_LINE, CITY, STATE];
const LANDING_PAGE_HOME = 'landing-page.home';

const formatError = (error) => {
    if (Array.isArray(error)) {
        return error.join('. ');
    }
    return error;
};

export default {
    name: "CustomAddressTypeAheadMixin",
    mixins: [
        CityAndStateMixin,
        ShowNotificationMixin,
    ],
    props: {
        value: {
            type: Object,
            default: () => {
            }
        },
        errors: {
            type: Object,
            default: () => {
                return {}
            }
        },
        canAddCustomAddress: {
            type: Boolean,
            default: true,
        },
        small: {
            type: Boolean,
            default: false,
        },
        scrollSettings: {
            type: Object,
            default: () => {
                return {
                    wheelPropagation: false,
                };
            }
        },
        prefillAddress: {
            type: Boolean,
            default: false,
        },
        shouldDisplayPreselectedAddress: {
            type: Boolean,
            default: false,
        },
        allowPreselectedAddressCorrection: {
            type: Boolean,
            default: false,
        },
        swapTextClass: {
            type: String,
            default: '',
        },
        searchLabel: {
            type: String,
            default: 'Address - required',
        },
        cityStatePlaceholder: {
            type: String,
            default: 'City, example: Austin, TX',
        },
        addressRef: {
            type: String,
            default: '',
        },
        streetLabel: {
            type: String,
            default: 'Street Address - required',
        },
        cityStateLabel: {
            type: String,
            default: 'City & State',
        },
        zipLabel: {
            type: String,
            default: 'ZIP Code',
        },
        showLabel: {
            type: Boolean,
            default: true,
        },
        token: {
            type: String,
            default: null,
            required: false,
        },
        invitationKey: {
            type: String,
            default: null,
            required: false,
        },
        isOptInPendingInvitation: {
            type: Boolean,
            default: false,
        },
        disabled: {
            type: Boolean,
            default: false,
        },
        showEnterManuallyButton: {
            type: Boolean,
            default: true,
        },
        placeholder: {
            type: String,
            default: 'Enter address',
        },
        hasCustomBeforeSlot: {
            type: Boolean,
            default: false,
        },
        hasFocusableState: {
            type: Boolean,
            default: true,
        },
    },
    data() {
        return {
            isCustomAddressSelected: false,
            cityAndState: '',
            cities: new AppResourceCollection('city'),
            disabledIndex: undefined,
            firstStreet: '',
            secondStreet: '',
            zipCode: '',
            resetSearchedAddress: false,
            dropdownShow: true,
            refOutsideClickFn: null,
            addressResource: new AddressAppResource({attributes: {street: ''}}),
            addressOverwrittenLock: false,
            HJZipValidationFailed: false,
        };
    },

    computed: {
        getAddressRef() {
            if (this.addressRef) {
                return this.addressRef;
            }
            return generateRandomString(16);
        },
        preselectedAddress() {
            return this.shouldDisplayPreselectedAddress ? this.value : {};
        },
        preselectedAddressName() {
            let locationsAttributes = {};

            if (this.shouldDisplayPreselectedAddress && this.value?.attributes) {
                locationsAttributes = this.value.attributes;
            } else if (!this.shouldDisplayPreselecedAddress && this.addressResource?.attributes) {
                locationsAttributes = this.addressResource.attributes;
            }

            if (Object.keys(locationsAttributes).length === 0) {
                return '';
            }

            if (!locationsAttributes.deliveryLine) {
                locationsAttributes.deliveryLine = locationsAttributes.streetNumber
                    ? `${locationsAttributes.streetNumber} `
                    : '';
                locationsAttributes.deliveryLine += locationsAttributes.street || '';
            }

            const filteredAddressParts = ADDRESS_SEARCH_PARTS
                .map(param => locationsAttributes[param])
                .filter(value => !!value);

            let searchValue = filteredAddressParts.join(', ');
            searchValue += locationsAttributes.zip ? ` ${locationsAttributes.zip}` : '';
            this.$emit(IS_ZIP_CODE_VALID_EVENT, this.hasAddressValidCustomZipCode());

            return searchValue.trim();
        },
        shouldSearchPreselectedAddress() {
            return this.shouldDisplayPreselectedAddress &&
                this.allowPreselectedAddressCorrection;
        },
        isExternalInvitation() {
            return this.invitationKey != null && this.token != null;
        },
        isLandingPageHome() {
            return LANDING_PAGE_HOME === this.$route?.name && window.landingPageUuid?.length > 0;
        }
    },
    watch: {
        value: {
            deep: true,
            handler(newVal, oldVal) {
                if (newVal === null) {
                    this.changeToSearchAddressView()
                    return
                }

                if (isEmpty(oldVal) && isEmpty(newVal)) {
                    this.resetSearchedAddress = true;
                    return;
                }

                if (isEmpty(oldVal)) {
                    return;
                }

                if (
                    !this.isCustomAddressSelected &&
                    isEmpty(newVal.fullAddress) &&
                    !isEmpty(oldVal.fullAddress)
                ) {
                    this.resetSearchedAddress = true;
                    this.resetAddressSelection();
                }

                if (
                    this.isCustomAddressSelected &&
                    isEmpty(newVal.zip) &&
                    isEmpty(newVal.street) &&
                    isEmpty(newVal.city) &&
                    isEmpty(newVal.state) &&
                    isNaN(newVal.streetNumber) &&
                    (
                        !isEmpty(this.zipCode) ||
                        !isEmpty(this.firstStreet) ||
                        !isEmpty(this.secondStreet) ||
                        !isEmpty(this.cityAndState)
                    )
                ) {
                    this.changeToSearchAddressView();
                }
            }
        },
    },
    mounted() {
        const dropdownElement = document.getElementById('city-state-dropdown');
        if (dropdownElement) {
            this.refOutsideClickFn = (event) => {
                if (!dropdownElement.contains(event.target)) {
                    this.dropdownShow = false;
                }
            };
            document.addEventListener('click', this.refOutsideClickFn);
        }


    },
    beforeDestroy() {
        document.removeEventListener('click', this.refOutsideClickFn);
    },
    methods: {
        changeToCustomAddressView(overwriteAddress = false, addressAppResourceToPrefill = null) {
            this.isCustomAddressSelected = true;
            this.resetSearchedAddress = true;
            if (this.prefillAddress) {
                this.prefillAddressData(overwriteAddress, addressAppResourceToPrefill);
            } else {
                this.resetAddressSelection();
            }
        },
        changeToSearchAddressView() {
            this.resetCustomAddressFields();
            this.resetAddressSelection();
            this.isCustomAddressSelected = false;
            this.$emit('is-custom-address-selected', false);
        },
        prefillAddressData(overwriteAddress, addressAppResourceToPrefill = null) {
            if (addressAppResourceToPrefill) {
                this.addressResource = new AddressAppResource({
                    uuid: addressAppResourceToPrefill.uuid,
                    attributes: addressAppResourceToPrefill.attributes,
                });
            } else if (overwriteAddress && this.value instanceof AddressAppResource) {
                this.addressResource = new AddressAppResource({
                    uuid: this.value.uuid,
                    attributes: this.value.attributes,
                });
            }
            this.firstStreet = !isEmpty(this.addressResource?.deliveryLine) ?
                this.addressResource.deliveryLine :
                (this.addressResource.streetNumber ? this.addressResource.streetNumber + ' ' : '')
                + (this.addressResource.street ? this.addressResource.street : '');
            this.zipCode = isEmpty(this.addressResource.zip) ? null : this.addressResource.zip;
            if (!isEmpty(this.addressResource.city) && !isEmpty(this.addressResource.state)) {
                this.cityAndState = this.addressResource.city + ', ' + this.addressResource.state;
            }

            if (!overwriteAddress) {
                this.isCustomAddressComplete() && (this.addressOverwrittenLock = true);
                this.setStreetAddress();
            } else if (this.shouldSearchPreselectedAddress) {
                this.handleCustomAddressSelected();
            }
        },
        setStreetAddress: debounce(async function () {
            this.addressResource.setAttribute('street', `${this.firstStreet} ${this.secondStreet}`.trim());
            this.addressResource.setAttribute('deliveryLine', this.addressResource.get('street'));
            this.cleanOldAddressDetails();

            await this.handleCustomAddressSelected();
        }, 1700),
        cleanOldAddressDetails() {
            this.addressResource.setAttribute('fullAddress', '');
            this.addressResource.setAttribute('streetNumber', '');
            this.addressResource.setAttribute('preDirection', '');
            this.addressResource.setAttribute('postDirection', '');
            this.addressResource.setAttribute('suffix', '');
            this.addressResource.setAttribute('latitude', null);
            this.addressResource.setAttribute('longitude', null);
            this.addressResource.setAttribute('accuracy', null);
            this.addressResource.setAttribute('secondaryNumber', null);
        },
        setZipCode: debounce(async function () {
            this.addressResource.setAttribute('zip', this.zipCode);
            this.addressResource.setAttribute('latitude', null);
            this.addressResource.setAttribute('longitude', null);
            await this.handleCustomAddressSelected();
        }, 1700),
        handleSearchAddressSelection() {
            if (undefined === this.addressResource) {
                return;
            }

            if (this.addressResource.hasOwnProperty('isAddressFound') && !this.addressResource.isAddressFound) {
                this.$emit('update:errors', {'locations.notFound': 'Address not found, try it manually.'});
            }

            if (this.prefillAddress) {
                if (!isEmpty(this.addressResource.street) &&
                    !this.isCustomAddressSelected &&
                    this.addressResource.hasOwnProperty('isAddressFound') && !this.addressResource.isAddressFound
                ) {
                    this.changeToCustomAddressView(false);
                    this.$emit(IS_ZIP_CODE_VALID_EVENT, this.hasAddressValidCustomZipCode());
                    return;
                }
            }

            this.handleAddressSelected();
        },
        async handleCustomAddressSelected() {
            if (undefined === this.addressResource) {
                return;
            }

            this.cleanOldAddressDetails();
            if (this.isCustomAddressComplete() && this.hasZipCorrectFormat()) {
                this.$emit(IS_ZIP_CODE_VALID_EVENT, this.hasAddressValidCustomZipCode());
                await this.standardizeCustomAddress();
            }

            this.$emit('select-address', cloneDeep(this.addressResource));
            this.$emit(IS_ZIP_CODE_VALID_EVENT, this.hasAddressValidCustomZipCode());
        },
        handleAddressSelected() {
            this.$emit('select-address', cloneDeep(this.addressResource));
            this.$emit(IS_ZIP_CODE_VALID_EVENT, this.hasAddressValidZipCode());
        },
        resetAddressSelection() {
            this.addressResource = new AddressAppResource({});
            this.$emit('select-address', this.addressResource);
            this.$emit(IS_ZIP_CODE_VALID_EVENT, this.hasAddressValidZipCode());
        },
        resetCustomAddressFields() {
            this.secondStreet = '';
            this.firstStreet = '';
            this.cityAndState = '';
            this.zipCode = '';
        },
        async handleInputCityAndState(event) {
            if (isEmpty(event.trim())) {
                this.cityAndState = ``;
                this.addressResource.setAttribute('city', '');
                this.addressResource.setAttribute('state', '');
                await this.handleCustomAddressSelected();
                return;
            }
            this.dropdownShow = true;
            this.onInputCityAndState(event);
        },
        async handleSelectForCityAndState(cityAndStateSelected, disabledIndex) {
            this.disabledIndex = disabledIndex;
            this.cityAndState = `${cityAndStateSelected.name}, ${cityAndStateSelected.state}`;
            this.addressResource.setAttribute('city', cityAndStateSelected.name);
            this.addressResource.setAttribute('state', cityAndStateSelected.state);
            this.disabledIndex = undefined;

            await this.handleCustomAddressSelected();
        },
        handleNonClickableOption(event) {
            event.stopPropagation();
            event.preventDefault();
        },
        addCustomAddress(event) {
            event.stopPropagation();
            event.preventDefault();
            this.changeToCustomAddressView(true);
            this.$emit('is-custom-address-selected', true);
        },
        areAddressesIdentical() {
            return this.addressResource.city?.toLowerCase() === this.value.city?.toLowerCase() &&
                this.addressResource.state?.toLowerCase() === this.value.state?.toLowerCase() &&
                this.addressResource.deliveryLine?.toLowerCase() === this.value.deliveryLine?.toLowerCase() &&
                this.addressResource.zip === this.value.zip;
        },
        arePreselectedAddressCoordinatesAlreadyValid() {
            const isValid = !!this.value.latitude && !!this.value.longitude;
            this.$emit(IS_ZIP_CODE_VALID_EVENT, isValid);
            return isValid;
        },
        hasAddressValidZipCode() {
            return this.hasZipCorrectFormat();
        },
        hasAddressValidCustomZipCode() {
            return this.hasAddressValidZipCode();
        },
        hasZipCorrectFormat() {
            return !isEmpty(this.addressResource?.attributes?.zip) &&
                !isNaN(this.addressResource?.attributes?.zip) &&
                this.addressResource?.attributes?.zip?.length === ZIP_CODE_LENGTH;
        },
        areCoordinatesFound() {
            if (
                typeof this.addressResource?.attributes?.latitude === 'number' &&
                typeof this.addressResource?.attributes?.longitude === 'number'
            ) {
                return true;
            }

            return !!(this.addressResource?.attributes?.latitude && this.addressResource?.attributes?.longitude);
        },
        async standardizeCustomAddress() {
            let address = null;
            let params = {
                delivery_line: `${this.firstStreet} ${this.secondStreet}`.trim(),
                city: this.addressResource.city,
                state: this.addressResource.state,
                zip: this.addressResource.zip
            };

            let isPublic = this.isLandingPageHome || this.isExternalInvitation;

            try {
                if (!this.addressOverwrittenLock) {
                    address = await standardizeAddress(params, isPublic);
                }

                if (this.isCustomAddressSelected && address) {
                    this.addressResource.setAttribute('latitude', address?.latitude || null);
                    this.addressResource.setAttribute('longitude', address?.longitude || null);
                    this.addressResource.setAttribute('streetNumber', address?.street_number);
                    this.addressResource.setAttribute('street', address?.street);
                    this.addressResource.setAttribute('preDirection', address?.pre_direction);
                    this.addressResource.setAttribute('postDirection', address?.post_direction);
                    this.addressResource.setAttribute('suffix', address?.suffix);
                    this.addressResource.setAttribute('secondaryNumber', address?.secondary_number);
                    this.addressResource.setAttribute('postalCode', address?.postal_code || address?.zip);
                    this.addressResource.setAttribute('county', address?.county);
                    this.addressResource.setAttribute('zip', address?.postal_code || address?.zip);
                    this.addressResource.setAttribute('deliveryLine', address?.delivery_line);
                    this.addressResource.setAttribute('fullAddress', address?.full_address);
                    this.addressResource.setAttribute('state', address?.state);
                    this.addressResource.setAttribute('city', address?.city);
                    this.addressResource.setAttribute('country', address?.country);
                    this.addressResource.setAttribute('corelogicClip', address?.corelogic_clip);

                    if (this.hasStandardizedAddressDifferentDetails(address)) {
                        this.updateCustomAddressDetails(address);
                    }
                    this.addressOverwrittenLock && (this.addressOverwrittenLock = false);
                }
            } catch (e) {
                // Only show error notification if it's not a 401
                if (e?.response?.status !== 401) {
                    console.error(e);
                    this.showNotification({
                        message: 'Something went wrong',
                        type: 'error',
                    });
                }
            }
        },
        hasStandardizedAddressDifferentDetails(address) {
            return this.zipCode !== address?.postal_code ||
                `${this.firstStreet} ${this.secondStreet}`.trim() !== address?.delivery_line ||
                this.cityAndState !== `${address?.city}, ${address?.state}`;
        },
        isCustomAddressComplete() {
            return this.addressResource?.zip &&
                this.addressResource?.city &&
                this.addressResource?.state &&
                (this.addressResource?.street || this.addressResource?.deliveryLine);
        },
        updateCustomAddressDetails(address) {
            this.zipCode = address?.postal_code;
            this.firstStreet = address?.delivery_line;
            this.cityAndState = `${address?.city}, ${address?.state}`;
            this.secondStreet = '';
        }
    },
}
